import React, { useEffect, useRef, useState } from 'react';
import { cn } from '../../../lib/utils';

export const reduceMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export const money = (n, d) => {
  const v = Number(n || 0), a = Math.abs(v);
  if (a >= 1e6) return '$' + (v / 1e6).toFixed(d ?? (a >= 1e7 ? 0 : 1)).replace(/\.0$/, '') + 'M';
  if (a >= 1e3) return '$' + Math.round(v / 1e3) + 'k';
  return '$' + Math.round(v).toLocaleString('en-AU');
};
export const dollars = (n) => '$' + Math.round(Number(n || 0)).toLocaleString('en-AU');
export const pct0 = (p) => Math.round(Math.max(0, Math.min(1, p || 0)) * 100) + '%';
export const paceTone = (p, elapsed) => (p >= 1 ? 'green' : p >= elapsed - 0.08 ? 'blue' : p >= elapsed - 0.25 ? 'amber' : 'red');

/* Digits roll individually; punctuation stays static. */
export function Roll({ value, className, testid }) {
  const s = String(value ?? '');
  return (
    <span className={cn('gpj-roll', className)} data-testid={testid} data-value={s} aria-label={s}>
      {[...s].map((ch, i) => (/\d/.test(ch) ? (
        <span className="gpj-rd" key={i}>
          <span className="gpj-rc" style={{ transform: `translateY(-${Number(ch) * 1.05}em)` }}>
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => <span key={d}>{d}</span>)}
          </span>
        </span>
      ) : <span key={i}>{ch === ' ' ? '\u00a0' : ch}</span>))}
    </span>
  );
}

const C = 2 * Math.PI * 84, R0 = 84;

/* Eased progress 0..1 with ~1.5s ease-out; snaps under reduced motion. */
function useEased(target, ms = 1500) {
  const [p, setP] = useState(0);
  const ref = useRef({ p: 0, raf: 0 });
  useEffect(() => {
    const to = Math.max(0, Math.min(1, target || 0));
    if (reduceMotion()) { ref.current.p = to; setP(to); return undefined; }
    const from = ref.current.p, t0 = performance.now();
    cancelAnimationFrame(ref.current.raf);
    const tick = (now) => {
      const k = Math.min(1, (now - t0) / ms), e = 1 - Math.pow(1 - k, 4);
      const v = from + (to - from) * e;
      ref.current.p = v; setP(v);
      if (k < 1) ref.current.raf = requestAnimationFrame(tick);
    };
    ref.current.raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(ref.current.raf);
  }, [target, ms]);
  return p;
}

/* Pulses for 1.6s whenever `key` changes (after the first render). */
export function usePulse(key) {
  const [on, setOn] = useState(false);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return undefined; }
    setOn(true);
    const t = setTimeout(() => setOn(false), 1600);
    return () => clearTimeout(t);
  }, [key]);
  return on;
}

export function Ring({ p = 0, value, of, label, sub, tone = 'blue', size = 132, segments, bare, testid, className }) {
  const eased = useEased(segments ? 1 : p);
  const pulse = usePulse(value);
  const attn = tone === 'amber' || tone === 'red';
  const th = -Math.PI / 2 + 2 * Math.PI * eased;
  let acc = 0;
  const tot = segments ? segments.reduce((s, x) => s + x.v, 0) || 1 : 1;
  return (
    <div className={cn('gpj-gauge', tone !== 'blue' && `t-${tone}`, attn && 'attn', pulse && 'pulse', className)} style={{ '--size': `${size}px` }} data-testid={testid}>
      <div className="gpj-ring">
        <svg viewBox="-8 -8 216 216" role="img" aria-label={`${label || ''}: ${value} ${of || ''}`}>
          <circle className="orbit" cx="100" cy="100" r="99" strokeWidth="1.2" />
          <circle className="orbit2" cx="100" cy="100" r="99" strokeWidth="2" />
          <circle className="trk" cx="100" cy="100" r="84" strokeWidth="15" />
          {segments ? segments.filter((x) => x.v > 0).map((x, i) => {
            const len = Math.max(0, (x.v / tot) * C - 3), off = C / 4 - acc; acc += (x.v / tot) * C;
            return <circle key={i} className="seg" cx="100" cy="100" r="84" strokeWidth="15" stroke={x.c} strokeDasharray={`${len.toFixed(1)} ${C.toFixed(1)}`} strokeDashoffset={off.toFixed(1)} />;
          }) : (
            <>
              <circle className="prg" cx="100" cy="100" r="84" strokeWidth="15" strokeDasharray={`${Math.max(0.001, eased * C)} ${C}`} strokeDashoffset={C / 4} />
              {eased > 0.01 && <circle className="sweep" cx="100" cy="100" r="84" strokeWidth="15" />}
              {eased > 0.01 && <circle className="capdot" r="4.5" cx={(100 + R0 * Math.cos(th)).toFixed(2)} cy={(100 + R0 * Math.sin(th)).toFixed(2)} />}
            </>
          )}
        </svg>
        <div className="gpj-ring-c">
          <div className="gpj-rv"><Roll value={value} /></div>
          {of && <div className="gpj-rs">{of}</div>}
        </div>
      </div>
      {!bare && label && <div className="gpj-glabel">{label}</div>}
      {!bare && sub && <div className="gpj-gsub" dangerouslySetInnerHTML={{ __html: sub }} />}
    </div>
  );
}

/* Semicircle speedometer dial with ticks, eased fill and a gently swaying needle. */
export function Dial({ remain, done, target, label, green, testid }) {
  const p = target ? Math.min(1, done / target) : 1;
  const complete = remain <= 0;
  const ticks = [];
  for (let i = 0; i <= 10; i++) {
    const a = Math.PI + (Math.PI * i) / 10;
    ticks.push(<line key={i} className="tk" x1={(60 + 56 * Math.cos(a)).toFixed(1)} y1={(62 + 56 * Math.sin(a)).toFixed(1)} x2={(60 + 60 * Math.cos(a)).toFixed(1)} y2={(62 + 60 * Math.sin(a)).toFixed(1)} />);
  }
  return (
    <div className={cn('gpj-dial', green && 'green', complete && 'done')} data-testid={testid}>
      <svg viewBox="-2 -2 124 74" aria-hidden="true">
        {ticks}
        <path className="dt" d="M12 62 A48 48 0 0 1 108 62" strokeWidth="9" />
        <path className="dp" d="M12 62 A48 48 0 0 1 108 62" strokeWidth="9" pathLength="100" strokeDasharray={`${(p * 100).toFixed(2)} 100`} />
        <g className="ndl-g" style={{ transform: `rotate(${-90 + 180 * p}deg)` }}>
          <g className="ndl-s"><line className="ndl" x1="60" y1="62" x2="60" y2="22" /></g>
        </g>
        <circle className="hub" cx="60" cy="62" r="4" />
      </svg>
      <b><Roll value={complete ? '✓' : Math.max(0, remain)} /></b>
      <span>{label}</span>
      <em>{done}/{target} done</em>
    </div>
  );
}
